import type { ResolutorDeLugares } from '@mc/campos'
import { desplegarCorchetes } from './entrada'
import { type Campo, campoPorClave, camposVisibles, conCalculados, DEL_SISTEMA, type Formulario, nivelPedido, rangoDeEscala, recorrer, seEnseña, SIN_VALOR, type ValorDeLugar } from './campos'

/**
 * **La validación del navegador es comodidad; la que vale es la del servidor**
 * (CLAUDE.md §9). Esto es la del servidor, y la única: se ejecuta siempre en el
 * núcleo contra el esquema del formulario, diga lo que diga el tema.
 *
 * El tema puede usarla también para avisar antes de enviar, pero no la sustituye.
 */
export type ErrorDeCampo = { clave: string; mensaje: string }

const EMAIL = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i
const TELEFONO = /^[+]?[\d\s().-]{6,20}$/
const FECHA = /^\d{4}-\d{2}-\d{2}$/
const HORA = /^\d{2}:\d{2}(?::\d{2})?$/

const texto = (valor: unknown): string => (valor === null || valor === undefined ? '' : String(valor)).trim()
const lista = (valor: unknown): string[] => (Array.isArray(valor) ? valor.map((v) => String(v)) : texto(valor) === '' ? [] : [texto(valor)])

const valoresDeOpciones = (campo: Campo): string[] => (campo.opciones ?? []).map((o) => o.valor)

/** Una dirección web larga de verdad cabe de sobra; más allá, no es una dirección que escriba una persona. */
export const MAXIMO_DE_UNA_URL = 2000

/**
 * Una dirección web: **solo `http` y `https`**. `javascript:`, `data:` y compañía no son una
 * web, y esto acaba en un correo y en el panel, donde alguien hará clic. Sin esquema
 * («www.empresa.com») se entiende `https://`, que es lo que quiso decir quien la escribió.
 * Devuelve la dirección limpia, o `null` si no vale.
 */
export const direccionWeb = (escrita: string): null | string => {
  const texto = escrita.trim()
  if (texto === '' || texto.length > MAXIMO_DE_UNA_URL || /[\s\u0000-\u001f\u007f]/.test(texto)) return null
  const conEsquema = /^[a-z][a-z0-9+.-]*:/i.test(texto) ? texto : /^[\w-]+(\.[\w-]+)+([/?#:]|$)/.test(texto) ? `https://${texto}` : texto
  let url: URL
  try {
    url = new URL(conEsquema)
  } catch {
    return null
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
  if (!url.hostname || url.username || url.password) return null
  return conEsquema
}

/**
 * Los lugares oficiales (`@mc/lugares`), ya cargados, para validar un `lugar` **sin** que este
 * paquete los arrastre: son 1,5 MB y el tema también usa el paquete. Lo pone el núcleo, que es
 * quien valida de verdad. Sin él, un lugar elegido de la lista no se puede comprobar y no pasa.
 * Es el mismo contrato que el de los campos de datos (`@mc/campos`): se reexporta de allí.
 */
export type { ResolutorDeLugares }

const CODIGO_DE_LUGAR = /^[A-Za-z0-9._-]{1,20}$/
const MAXIMO_DEL_LUGAR_ESCRITO = 200

/**
 * Un lugar: el país (de los que admite el formulario; con uno solo, se pone él) y el código
 * oficial del nivel que se pide en ese país. **Lo que manda el navegador no se cree**: el
 * código tiene que ser de ese país y de ese nivel, y el camino lo escribe el servidor con
 * los nombres oficiales. Sin JavaScript no hay desplegables encadenados: se admite el lugar
 * escrito a mano (`texto`), que se guarda como lo que es.
 */
const validarLugar = (campo: Campo, bruto: unknown, lugares: ResolutorDeLugares | undefined): { error?: string; valor?: ValorDeLugar | null } => {
  const crudo = bruto && typeof bruto === 'object' && !Array.isArray(bruto) ? (bruto as Record<string, unknown>) : {}
  const permitidos = (campo.paises ?? []).filter((p) => /^[A-Z]{2}$/.test(p))
  let pais = texto(crudo.pais).toUpperCase()
  const codigo = texto(crudo.codigo)
  const escrito = texto(crudo.texto).replace(/\s+/g, ' ')
  if (permitidos.length === 1) {
    if (pais !== '' && pais !== permitidos[0]) return { error: 'Ese país no está entre los que admite este formulario.' }
    pais = permitidos[0]!
  }
  if (bruto !== null && bruto !== undefined && bruto !== '' && (typeof bruto !== 'object' || Array.isArray(bruto))) return { error: 'Elige el lugar de la lista.' }
  if (pais === '') return codigo || escrito || campo.obligatorio ? { error: 'Elige el país.' } : { valor: null }
  if (permitidos.length > 0 && !permitidos.includes(pais)) return { error: 'Ese país no está entre los que admite este formulario.' }
  const delPais = /^[A-Z]{2}$/.test(pais) ? (lugares?.pais(pais) ?? null) : null
  if (!delPais) {
    if (!lugares && codigo === '' && escrito === '' && !campo.obligatorio) return { valor: null }
    return { error: lugares ? 'Ese país no está en la lista.' : 'No hemos podido comprobar el lugar. Vuelve a intentarlo.' }
  }

  const pedido = nivelPedido(campo.nivel, delPais.niveles.length)
  // Un país sin divisiones: la respuesta es el país.
  if (pedido === 0) return { valor: { pais, codigo: null, camino: delPais.nombre } }
  const nombreDelNivel = delPais.niveles[pedido - 1] ?? 'Lugar'
  if (codigo !== '') {
    const lugar = CODIGO_DE_LUGAR.test(codigo) ? lugares!.lugar(pais, codigo) : null
    if (!lugar) return { error: 'Ese lugar no está en la lista. Elígelo otra vez.' }
    // El del nivel pedido; o uno de más arriba que no tiene nada debajo (no hay más que elegir).
    if (lugar.nivel > pedido) return { error: 'Ese lugar no está en la lista. Elígelo otra vez.' }
    if (lugar.nivel < pedido && !lugar.hoja) return { error: `Falta elegir: ${nombreDelNivel}.` }
    return { valor: { pais, codigo, camino: [delPais.nombre, ...lugar.nombres].join(' › ') } }
  }
  if (escrito !== '') {
    if (escrito.length > MAXIMO_DEL_LUGAR_ESCRITO) return { error: `Como mucho ${MAXIMO_DEL_LUGAR_ESCRITO} caracteres.` }
    return { valor: { pais, codigo: null, texto: escrito, camino: `${delPais.nombre} › ${escrito}` } }
  }
  // Solo el país: sin lugar no es una respuesta.
  return campo.obligatorio ? { error: `Falta elegir: ${nombreDelNivel}.` } : { valor: null }
}

/** Un campo, con su valor ya recortado. Devuelve el error o el valor limpio. */
const validarCampo = (campo: Campo, bruto: unknown, lugares?: ResolutorDeLugares): { error?: string; valor?: unknown } => {
  const obligatorio = Boolean(campo.obligatorio)
  if (campo.tipo === 'lugar') return validarLugar(campo, bruto, lugares)
  if (campo.tipo === 'casillas' || campo.tipo === 'opcion-multiple') {
    const elegidas = lista(bruto)
    const admitidas = valoresDeOpciones(campo)
    const fuera = elegidas.filter((v) => !admitidas.includes(v))
    if (fuera.length > 0) return { error: 'Esa opción no está entre las que se pueden elegir.' }
    if (campo.tipo === 'opcion-multiple' && elegidas.length > 1) return { error: 'Elige una sola opción.' }
    if (obligatorio && elegidas.length === 0) return { error: 'Elige una opción.' }
    return { valor: campo.tipo === 'opcion-multiple' ? (elegidas[0] ?? null) : elegidas }
  }

  if (campo.tipo === 'consentimiento') {
    const aceptado = bruto === true || bruto === 'true' || bruto === 'on' || bruto === '1'
    if (obligatorio && !aceptado) return { error: 'Hay que aceptarlo para poder enviar el formulario.' }
    return { valor: aceptado }
  }

  if (campo.tipo === 'archivo') {
    const archivos = Array.isArray(bruto) ? bruto : bruto ? [bruto] : []
    if (obligatorio && archivos.length === 0) return { error: 'Adjunta un archivo.' }
    const maximo = campo.maximoDeArchivos ?? 1
    if (archivos.length > maximo) return { error: `Como mucho ${maximo} archivo${maximo === 1 ? '' : 's'}.` }
    return { valor: archivos }
  }

  // Un objeto o una lista donde se espera un texto no es una respuesta: no se guarda «[object Object]».
  if (bruto !== null && typeof bruto === 'object') return { error: 'Escribe una respuesta.' }
  const valor = texto(bruto)
  if (valor === '') {
    if (obligatorio) return { error: campo.tipo === 'escala' ? 'Elige un número.' : 'Rellena este campo.' }
    return { valor: null }
  }

  switch (campo.tipo) {
    case 'url': {
      const limpia = direccionWeb(valor)
      if (!limpia) return { error: 'Escribe una dirección web con esta forma: https://www.ejemplo.com.' }
      return { valor: limpia }
    }
    case 'escala': {
      const { minimo, maximo } = rangoDeEscala(campo)
      const n = /^\d{1,2}$/.test(valor) ? Number(valor) : Number.NaN
      if (!Number.isInteger(n) || n < minimo || n > maximo) return { error: `Elige un número del ${minimo} al ${maximo}.` }
      return { valor: n }
    }
    case 'email':
      if (!EMAIL.test(valor)) return { error: 'Escribe un correo con esta forma: nombre@dominio.com.' }
      break
    case 'telefono':
      if (!TELEFONO.test(valor)) return { error: 'Escribe un teléfono: solo números, espacios y los signos + ( ) -.' }
      break
    case 'numero':
    case 'valoracion': {
      const n = Number(valor.replace(',', '.'))
      if (!Number.isFinite(n)) return { error: 'Escribe un número.' }
      if (campo.minimo !== null && campo.minimo !== undefined && n < campo.minimo) return { error: `Tiene que ser ${campo.minimo} o más.` }
      if (campo.maximo !== null && campo.maximo !== undefined && n > campo.maximo) return { error: `Tiene que ser ${campo.maximo} o menos.` }
      return { valor: n }
    }
    case 'fecha':
      if (!FECHA.test(valor) || Number.isNaN(Date.parse(valor))) return { error: 'Escribe una fecha con esta forma: 2026-03-15.' }
      break
    case 'hora':
      if (!HORA.test(valor)) return { error: 'Escribe una hora con esta forma: 09:30.' }
      break
    case 'desplegable':
      if (!valoresDeOpciones(campo).includes(valor)) return { error: 'Esa opción no está entre las que se pueden elegir.' }
      break
    default:
      break
  }

  const minimo = campo.minimoDeCaracteres
  const maximo = campo.maximoDeCaracteres ?? (campo.tipo === 'area' ? 5000 : 500)
  if (minimo && valor.length < minimo) return { error: `Escribe al menos ${minimo} caracteres.` }
  if (valor.length > maximo) return { error: `Como mucho ${maximo} caracteres.` }
  return { valor }
}

export type Contexto = {
  /** De dónde salen los campos ocultos (§9). */
  pagina?: null | string
  referente?: null | string
  utm?: Record<string, string | undefined>
  /** Los lugares oficiales, para validar los campos `lugar` (`ResolutorDeLugares`). */
  lugares?: ResolutorDeLugares
}

export type Resultado = { ok: false; errores: ErrorDeCampo[] } | { ok: true; valores: Record<string, unknown> }

const valorOculto = (campo: Campo, contexto: Contexto): null | string => {
  switch (campo.origen) {
    case 'pagina':
      return contexto.pagina ?? null
    case 'referente':
      return contexto.referente ?? null
    case 'valor-fijo':
      return campo.valorFijo ?? null
    case 'utm_source':
    case 'utm_medium':
    case 'utm_campaign':
    case 'utm_term':
    case 'utm_content':
      return contexto.utm?.[campo.origen] ?? null
    default:
      return campo.valorFijo ?? null
  }
}

/**
 * Valida un envío entero contra el esquema del formulario y devuelve los valores
 * limpios, que es lo que se guarda. Lo que no está en el esquema **se descarta**:
 * un campo que el formulario no declara no entra en la base de datos.
 *
 * Los campos escondidos por la lógica condicional no se piden ni se guardan; los
 * ocultos y los calculados los pone el servidor, se manden como se manden.
 */
export const validarEnvio = (formulario: Formulario, entrada: Record<string, unknown>, contexto: Contexto = {}): Resultado => {
  const errores: ErrorDeCampo[] = []
  const valores: Record<string, unknown> = {}

  // Primero lo que el visitante manda, para que la lógica condicional tenga con qué decidir.
  const declarados = new Map<string, Campo>()
  for (const campo of recorrer(formulario.campos)) if (campo.clave) declarados.set(campo.clave, campo)
  const propuestos: Record<string, unknown> = {}
  for (const [clave, valor] of Object.entries(desplegarCorchetes(entrada))) if (declarados.has(clave)) propuestos[clave] = valor

  // Los lugares se resuelven antes que nada: una condición sobre un lugar mira su camino
  // («contiene Lima»), y ese camino lo escribe el servidor, no el navegador.
  const lugaresResueltos = new Map<string, { error?: string; valor?: unknown }>()
  for (const campo of formulario.campos) {
    if (campo.tipo !== 'lugar' || !campo.clave) continue
    const resultado = validarLugar(campo, propuestos[campo.clave], contexto.lugares)
    lugaresResueltos.set(campo.clave, resultado)
    if (!resultado.error) propuestos[campo.clave] = resultado.valor ?? null
  }

  const validarLista = (campos: Campo[], datos: Record<string, unknown>, prefijo = ''): Record<string, unknown> => {
    const salida: Record<string, unknown> = {}
    for (const campo of campos) {
      if (SIN_VALOR.includes(campo.tipo)) continue
      const clave = campo.clave
      if (!clave) continue
      if (!seEnseña(campo, { ...propuestos, ...datos })) continue

      if (campo.tipo === 'oculto') {
        salida[clave] = valorOculto(campo, contexto)
        continue
      }
      if (campo.tipo === 'calculado') continue // lo pone `conCalculados`
      if (campo.tipo === 'pago') continue // lo pone el servidor con `importeDelPago`, después de validar

      if (campo.tipo === 'repetidor') {
        const filas = Array.isArray(datos[clave]) ? (datos[clave] as Array<Record<string, unknown>>) : []
        const maximo = campo.maximoDeFilas ?? 20
        if (filas.length > maximo) {
          errores.push({ clave: prefijo + clave, mensaje: `Como mucho ${maximo} filas.` })
          continue
        }
        if (campo.obligatorio && filas.length === 0) errores.push({ clave: prefijo + clave, mensaje: 'Añade al menos una fila.' })
        salida[clave] = filas.map((fila, i) => validarLista(campo.campos ?? [], fila && typeof fila === 'object' ? fila : {}, `${prefijo}${clave}.${i}.`))
        continue
      }

      const { error, valor } = (prefijo === '' ? lugaresResueltos.get(clave) : undefined) ?? validarCampo(campo, datos[clave], contexto.lugares)
      if (error) errores.push({ clave: prefijo + clave, mensaje: error })
      else salida[clave] = valor ?? null
    }
    return salida
  }

  Object.assign(valores, validarLista(formulario.campos, propuestos))
  if (errores.length > 0) return { ok: false, errores }
  return { ok: true, valores: conCalculados(formulario.campos, valores) }
}

/** Qué campos pide el formulario con estos valores: para el tema y para la prueba de que los pinta todos. */
export const camposPedidos = (formulario: Formulario, valores: Record<string, unknown> = {}): Campo[] =>
  camposVisibles(formulario.campos, valores).filter((campo) => !DEL_SISTEMA.includes(campo.tipo))

export { campoPorClave }
